import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';
import TableDrilldown from '../components/TableDrilldown';

const TablesTab: React.FC<{ projectId?: number }> = ({ projectId }) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['obs-tables', projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.listTables(projectId ? { project_id: projectId } : {})).data.data,
    refetchInterval: 60000,
  });

  if (selectedId != null) {
    return <TableDrilldown tableId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="bg-white rounded-xl border border-cargo-border overflow-hidden">
      <div className="px-5 py-3 border-b border-cargo-border flex items-center justify-between">
        <h3 className="font-semibold">Monitored Tables</h3>
        <div className="text-xs text-cargo-muted">Auto-refresh: 15s</div>
      </div>
      {q.isLoading ? (
        <div className="p-8 text-center text-sm text-cargo-muted">Loading...</div>
      ) : (q.data || []).length === 0 ? (
        <div className="p-10 text-center">
          <div className="text-cargo-muted text-sm mb-3">No tables are being monitored yet.</div>
          <div className="text-xs text-cargo-muted">
            Register tables via <code className="px-1.5 py-0.5 bg-gray-100 rounded">POST /api/observability/tables</code>
            {' '}or run the onboarding wizard to auto-register.
          </div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-cargo-muted">
            <tr>
              <th className="text-left px-5 py-2.5">Table</th>
              <th className="text-left px-5 py-2.5">Status</th>
              <th className="text-right px-5 py-2.5">Row Count</th>
              <th className="text-right px-5 py-2.5">Staleness</th>
              <th className="text-left px-5 py-2.5">Last Anomaly</th>
            </tr>
          </thead>
          <tbody>
            {(q.data || []).map((t: any) => (
              <tr
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                className="border-t border-cargo-border cursor-pointer hover:bg-gray-50"
              >
                <td className="px-5 py-3 font-medium">
                  {t.schema_name ? `${t.schema_name}.` : ''}{t.table_name}
                </td>
                <td className="px-5 py-3">
                  <StatusPill status={t.status} />
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {t.latest_row_count != null ? Number(t.latest_row_count).toLocaleString() : '—'}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {t.latest_staleness_seconds != null ? formatStale(t.latest_staleness_seconds) : '—'}
                </td>
                <td className="px-5 py-3 text-xs text-cargo-muted">
                  {t.last_anomaly ? (
                    <>
                      <span className="font-semibold uppercase mr-1.5 text-red-600">
                        {t.last_anomaly.severity}
                      </span>
                      {t.last_anomaly.pillar}/{t.last_anomaly.metric}
                    </>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    anomaly: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    healthy: 'bg-emerald-100 text-emerald-700',
  };
  return <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status || 'unknown'}</span>;
};

function formatStale(sec: number): string {
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export default TablesTab;
